<script>
import { SancareOcticon } from '@sancare/typescript-commons'
import _ from 'lodash'
import { mapState } from 'vuex'

import { CountryEnum } from '@/store/modules/settings/types'

import InitialDiagnosis from '../InitialDiagnosis.vue'
import ManualDiagnosisButton from '../ManualDiagnosisButton.vue'
import SuggestedDiagnosisCards from '../SuggestedDiagnosisCards.vue'
import SuggestedDiagnosisList from '../SuggestedDiagnosisList.vue'

export default {
  components: {
    'suggested-diagnosis-list': SuggestedDiagnosisList,
    'suggested-diagnosis-cards': SuggestedDiagnosisCards,
    'manual-diagnosis-button': ManualDiagnosisButton,
    'initial-diagnosis': InitialDiagnosis,
    'sancare-octicon': SancareOcticon,
  },
  props: {
    appSettings: { type: Object, required: true },
    sancareSettings: { type: Object, required: true },
    mode: { type: String, required: true },
    suggestedDiagnoses: { type: Array, required: true },
    rumId: { type: Number, required: true },
    standardModeDiagnosisGroup: { type: Object, required: true },
    ghm: { type: Object, default: null },
    healthData: { type: Object, required: true },
    selection: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    suggestionJustifications: { type: Object, default: null },
    staySavedSearches: { type: Array, default: null },
    standardModeExcludedDiagnoses: { type: Array, required: true },
    displayedRumIdx: { type: Number, required: true },
    type: { type: String, default: null },
  },
  emits: ['make-selection'],
  data() {
    return {
      extendedSuggestions: [],
      CountryEnum,
    }
  },
  computed: {
    ...mapState({
      suggestedDasDisplayMode: (state) => state.stayDisplayer.suggestedDasDisplayMode,
      pmsiCountry: (state) => state.settings.pmsi.country,
    }),
    stayEndDate() {
      return this.$store.state.stayDisplayer.displayedStay.stayEnd
    },
    displayedDiagnoses() {
      // hide disabled DAS from CurrentCoding
      const displayableAssociatedDiagnoses = this.standardModeDiagnosisGroup.associatedDiags.filter((das) => !das.disabled)
      return _.orderBy(
        displayableAssociatedDiagnoses,
        ['associatedDiagnosis.level', 'excluded', 'associatedDiagnosis.reference'],
        ['desc', 'asc', 'asc']
      )
    },
    displayedExternalDiagnoses() {
      // return all CurrentCoding diagnoses that come from outside the UI
      return _.filter(this.displayedDiagnoses, (diag) => {
        return this.isExternalDiagnosis(diag) && !diag.disabled
      })
    },
    displayedInternalDiagnoses() {
      // return all CurrentCoding diagnoses that have been coded on the UI
      return _.filter(this.displayedDiagnoses, (diag) => {
        return !this.isExternalDiagnosis(diag)
      })
    }
  },
  methods: {
    getSuggestionFromInitialDiagnosis(currentDiagnosis) {
      const suggestion =  _.find(this.suggestedDiagnoses, (suggestion) => {
        if(!suggestion.isWildcard){
          return suggestion.reference === currentDiagnosis.associatedDiagnosis.reference
        }
        return _.some(suggestion.diagnoses, (diag) => diag.reference === currentDiagnosis.associatedDiagnosis.reference)
      })
      return suggestion || null
    },
    isExternalDiagnosis(diagnosis) {
      return ['external', 'unsure', 'prediction_unsure'].indexOf(diagnosis.codingOrigin) !== -1
    },
    addDiagnosis(suggestedDiagnosis) {
      this.$store.dispatch('addDiagnosis', {
        rumId: this.rumId,
        diagnosisType: 'DAS',
        reference: suggestedDiagnosis.reference,
        justifications: null,
      })
    },
    removeDAS(diagId) {
      this.$store.dispatch('removeDAS', { diagId: diagId })
    },
    switchSuggestedDasDisplayMode() {
      this.$store.commit('setSuggestedDasDisplayMode', this.suggestedDasDisplayMode === 'list' ? 'cards' : 'list')
    },
    getSearchesIds(diagnosis) {
      return _.map(diagnosis.staySavedSearches, (ss) => ss.search.id)
    },
    stringifySuggestionSearches(suggestion, sep, cut = false) {
      const content = _.map([...suggestion.staySavedSearches], (savedSearch) => savedSearch.search.name).join(sep)
      if (cut) {
        return content.substring(0, 20)
      }
      return content
    },
    toggleSuggestionDiagnoses(suggestion) {
      if (suggestion.isWildcard) {
        if (this.isExtendedSuggestion(suggestion)) {
          this.extendedSuggestions = _.filter(this.extendedSuggestions, (ref) => ref !== suggestion.reference)
        } else {
          this.extendedSuggestions.push(suggestion.reference)
        }
      }
    },
    isExtendedSuggestion(suggestion) {
      return this.extendedSuggestions.indexOf(suggestion.reference) !== -1
    },
    makeSelection(selection) {
      this.$emit('make-selection', selection)
    },
    readOnly() {
      return this.rumId === 0
    },
  }
}
</script>

<template>
  <div class="row g-0 gap-3 align-items-start">
    <div class="col">
      <div class="card diagnosis-card highlighted">
        <div class="card-body">
          <h1 class="card-title d-flex align-items-center">
            <span>{{ $t('diagnosis.'+type) }}</span>
            <small class="ms-1 fs-6">- {{ $t('stayDisplayer.mco.diagnosisStatus.current') }}</small>
          </h1>
        </div>

        <div
          v-if="!displayedDiagnoses.length"
          class="card-body"
        >
          <h2>Pas de {{ $t('diagnosis.'+type) }}</h2>
        </div>
        <div
          v-for="currentDiagnosis in displayedExternalDiagnoses"
          :key="currentDiagnosis.id"
          class="card-body diagnosis-initial"
        >
          <div
            :class="{ excluded: currentDiagnosis.excluded }"
            class="row g-0 align-items-center diagnosis-item"
          >
            <div
              :data-reference="currentDiagnosis.reference"
              :title="currentDiagnosis.associatedDiagnosis.description"
              class="col d-flex"
            >
              <h2 class="diagnosis-reference">
                {{ currentDiagnosis.reference }}
              </h2>
              <div
                v-if="'level' in currentDiagnosis.associatedDiagnosis && pmsiCountry === CountryEnum.FR"
                class="diagnosis-level ms-1"
              >
                <span v-if="!currentDiagnosis.excluded">{{ $t('diagnosis.level') }}. {{ currentDiagnosis.associatedDiagnosis.level }}</span>
                <span v-else>{{ $t('diagnosis.excluded') }}</span>
              </div>
            </div>
            <div
              v-if="!readOnly()"
              title="Supprimer le diagnostic"
              class="col-auto predicted-diagnosis-remover"
              @click.stop="removeDAS(currentDiagnosis.id)"
            >
              <sancare-octicon
                name="x"
                :width="18"
                :height="18"
              />
            </div>
          </div>
        </div>
        <div
          v-for="currentDiagnosis in displayedInternalDiagnoses"
          :key="currentDiagnosis.id"
          class="card-body diagnosis-initial"
        >
          <initial-diagnosis
            :app-settings="appSettings"
            :type="type"
            :is-loading="isLoading"
            :selected="selection !== null && selection.labelType === type && selection.labelReference === currentDiagnosis.associatedDiagnosis.reference"
            :selection="selection"
            :initial-diagnosis="currentDiagnosis"
            :suggestion="getSuggestionFromInitialDiagnosis(currentDiagnosis)"
            :health-data="healthData"
            :suggestion-justifications="suggestionJustifications"
            :readonly="readOnly()"
            @make-selection="makeSelection"
            @remove-das="removeDAS"
          />
        </div>
      </div>
      <div class="row g-0 pb-2">
        <manual-diagnosis-button
          :type="'DAS'"
          :context-date="stayEndDate"
          :read-only="readOnly()"
          :can-add-uncommon-diagnoses="pmsiCountry === CountryEnum.FR"
          @add-diagnosis="reference => addDiagnosis({ reference })"
        />
      </div>
    </div>
    <component
      :is="'suggested-diagnosis-' + suggestedDasDisplayMode"
      class="card diagnosis-card col"
      :app-settings="appSettings"
      :type="type"
      :is-loading="isLoading"
      :mode="mode"
      :selection="selection"
      :suggested-diagnoses="suggestedDiagnoses"
      :rum-id="rumId"
      :standard-mode-diagnosis-group="standardModeDiagnosisGroup"
      :health-data="healthData"
      :suggestion-justifications="suggestionJustifications"
      :displayed-rum-idx="displayedRumIdx"
      :readonly="readOnly()"
      @switch-display-mode="switchSuggestedDasDisplayMode()"
      @make-selection="makeSelection"
    />
  </div>
</template>

<style lang="scss" scoped>
.card-title {
  font-size: 2rem;
}

h2 {
  font-size: 1.1rem;
}
</style>
